import { Type } from '@angular/core';
import { DashboardWidgetDefinition, IDashboardWidget } from 'src/app/models/dashboard';
import { DashboardWidgetType } from 'src/app/enums/dashboardWidgetType.enum';
import { GridWidgetComponent } from './grid-widget/grid-widget.component';
import { SampleStatusWidgetComponent } from './sample-status-widget/sample-status-widget.component';
import { ExceedanceComplianceWidgetComponent } from './exceedance-compliance-widget/exceedance-compliance-widget.component';
import { ExceedanceComplianceWidgetConfigComponent } from './exceedance-compliance-widget/exceedance-compliance-widget-config.component';
import { SampleStatusWidgetConfigComponent } from './sample-status-widget/sample-status-widget-config.component';
import { EquipmentCalibrationStatusWidgetComponent } from './equipment-calibration-status-widget/equipment-calibration-status-widget.component';
import { SurveyProgressWidgetComponent } from './survey-progress-widget/survey-progress-widget.component';
import { SampleCollectionMetricsWidgetComponent } from './sample-collection-metrics-widget/sample-collection-metrics-widget.component';
import { SampleCollectionMetricsWidgetConfigComponent } from './sample-collection-metrics-widget/sample-collection-metrics-widget-config.component';
import { SegProfileSummaryWidgetComponent } from './seg-profile-summary-widget/seg-profile-summary-widget.component';
import { SegProfileSummaryWidgetConfigComponent } from './seg-profile-summary-widget/seg-profile-summary-widget-config.component';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';

export const WIDGET_TYPE_KEYS = {
  GRID_VIEW: 'grid-view',
  SAMPLE_STATUS_PIPELINE: 'sample-status-pipeline',
  EXCEEDANCE_COMPLIANCE: 'exceedance-compliance',
  EQUIPMENT_CALIBRATION_STATUS: 'equipment-calibration-status',
  SURVEY_PROGRESS: 'survey-progress',
  SAMPLE_COLLECTION_METRICS: 'sample-collection-metrics',
  SEG_PROFILE_SUMMARY: 'seg-profile-summary',
} as const;

const WIDGET_TYPE_STRING_MAP: Record<string, DashboardWidgetType> = {
  [WIDGET_TYPE_KEYS.GRID_VIEW]: DashboardWidgetType.GridView,
  [WIDGET_TYPE_KEYS.SAMPLE_STATUS_PIPELINE]: DashboardWidgetType.StatisticsCard,
  [WIDGET_TYPE_KEYS.EXCEEDANCE_COMPLIANCE]: DashboardWidgetType.StatisticsCard,
  [WIDGET_TYPE_KEYS.EQUIPMENT_CALIBRATION_STATUS]: DashboardWidgetType.Chart,
  [WIDGET_TYPE_KEYS.SURVEY_PROGRESS]: DashboardWidgetType.StatisticsCard,
  [WIDGET_TYPE_KEYS.SAMPLE_COLLECTION_METRICS]: DashboardWidgetType.Chart,
  [WIDGET_TYPE_KEYS.SEG_PROFILE_SUMMARY]: DashboardWidgetType.StatisticsCard,
};

const WIDGET_TYPE_ENUM_MAP: Partial<Record<DashboardWidgetType, string>> = Object.entries(WIDGET_TYPE_STRING_MAP)
  .reduce((map, [key, enumVal]) => {
    if (!(enumVal in map)) {
      map[enumVal] = key;
    }
    return map;
  }, {} as Partial<Record<DashboardWidgetType, string>>);

export const DASHBOARD_WIDGET_REGISTRY: Record<string, DashboardWidgetDefinition> = {
  [WIDGET_TYPE_KEYS.GRID_VIEW]: {
    type: WIDGET_TYPE_KEYS.GRID_VIEW,
    name: 'Grid View',
    description: 'Display any entity in a customizable grid view',
    icon: 'grid_on',
    category: 'other',
    component: GridWidgetComponent,
    minWidth: 2,
    minHeight: 2,
    defaultWidth: 2,
    defaultHeight: 2,
    configSchema: []
  },
  [WIDGET_TYPE_KEYS.SAMPLE_STATUS_PIPELINE]: {
    type: WIDGET_TYPE_KEYS.SAMPLE_STATUS_PIPELINE,
    name: 'Sample Workflow Pipeline',
    description: 'Display sample counts by workflow status with color-coded cards',
    icon: 'account_tree',
    category: 'statistics',
    component: SampleStatusWidgetComponent,
    configComponent: SampleStatusWidgetConfigComponent,
    minWidth: 2,
    minHeight: 2,
    defaultWidth: 2,
    defaultHeight: 2,
    configSchema: [],
    requiredPermission: RolePermissionObjects.Sample
  },
  [WIDGET_TYPE_KEYS.EXCEEDANCE_COMPLIANCE]: {
    type: WIDGET_TYPE_KEYS.EXCEEDANCE_COMPLIANCE,
    name: 'Exceedance Compliance',
    description: 'Track compliance rates and exceedances against OEL standards by agent',
    icon: 'health_and_safety',
    category: 'statistics',
    component: ExceedanceComplianceWidgetComponent,
    configComponent: ExceedanceComplianceWidgetConfigComponent,
    minWidth: 2,
    minHeight: 2,
    defaultWidth: 3,
    defaultHeight: 2,
    configSchema: [],
    requiredPermission: RolePermissionObjects.Sample
  },
  [WIDGET_TYPE_KEYS.EQUIPMENT_CALIBRATION_STATUS]: {
    type: WIDGET_TYPE_KEYS.EQUIPMENT_CALIBRATION_STATUS,
    name: 'Equipment Calibration Status',
    description: 'Display equipment calibration status with donut chart and overdue/upcoming lists',
    icon: 'build_circle',
    category: 'charts',
    component: EquipmentCalibrationStatusWidgetComponent,
    minWidth: 1,
    minHeight: 2,
    defaultWidth: 2,
    defaultHeight: 2,
    configSchema: [],
    requiredPermission: RolePermissionObjects.CalibrationEquipment
  },
  [WIDGET_TYPE_KEYS.SURVEY_PROGRESS]: {
    type: WIDGET_TYPE_KEYS.SURVEY_PROGRESS,
    name: 'Survey Progress',
    description: 'Display active surveys with progress tracking',
    icon: 'poll',
    category: 'statistics',
    component: SurveyProgressWidgetComponent,
    minWidth: 2,
    minHeight: 2,
    defaultWidth: 2,
    defaultHeight: 2,
    configSchema: [],
    requiredPermission: [RolePermissionObjects.Survey, RolePermissionObjects.Sample]
  },
  [WIDGET_TYPE_KEYS.SAMPLE_COLLECTION_METRICS]: {
    type: WIDGET_TYPE_KEYS.SAMPLE_COLLECTION_METRICS,
    name: 'Sample Collection Metrics',
    description: 'Track sample collection volume with year-over-year trend analysis and stacked breakdowns',
    icon: 'trending_up',
    category: 'charts',
    component: SampleCollectionMetricsWidgetComponent,
    configComponent: SampleCollectionMetricsWidgetConfigComponent,
    minWidth: 2,
    minHeight: 2,
    defaultWidth: 3,
    defaultHeight: 3,
    configSchema: [],
    requiredPermission: RolePermissionObjects.Sample
  },
  [WIDGET_TYPE_KEYS.SEG_PROFILE_SUMMARY]: {
    type: WIDGET_TYPE_KEYS.SEG_PROFILE_SUMMARY,
    name: 'SEG Profile Summary',
    description: 'Display SEG exposure bands and decision statistics from the current Risk Assessment',
    icon: 'analytics',
    category: 'statistics',
    component: SegProfileSummaryWidgetComponent,
    configComponent: SegProfileSummaryWidgetConfigComponent,
    minWidth: 2,
    minHeight: 2,
    defaultWidth: 3,
    defaultHeight: 3,
    configSchema: [],
    requiredPermission: RolePermissionObjects.RiskAssessment
  }
};

export const getAllWidgetDefinitions = (): DashboardWidgetDefinition[] => Object.values(DASHBOARD_WIDGET_REGISTRY);

export const getWidgetDefinition = (type: string): DashboardWidgetDefinition | undefined => DASHBOARD_WIDGET_REGISTRY[type];

export const getWidgetsByCategory = (category: string): DashboardWidgetDefinition[] =>
  getAllWidgetDefinitions().filter(w => w.category === category);

export const isValidWidgetType = (type: string): boolean => type in DASHBOARD_WIDGET_REGISTRY;

export const getWidgetComponent = (type: string): Type<IDashboardWidget> | undefined => DASHBOARD_WIDGET_REGISTRY[type]?.component;

export const getWidgetTypeFromString = (type: string): DashboardWidgetType => WIDGET_TYPE_STRING_MAP[type] ?? DashboardWidgetType.GridView;

export const getWidgetTypeString = (type: DashboardWidgetType): string => WIDGET_TYPE_ENUM_MAP[type] ?? WIDGET_TYPE_KEYS.GRID_VIEW;
